import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

export type NavigationPanelSection = "dms" | "projects" | "contacts";

interface DesktopNavigationContextType {
  isNavigationBarOpen: boolean;
  toggleNavigationBar: () => void;
  setIsNavigationBarOpen: (open: boolean) => void;
  panelSection: NavigationPanelSection;
  setPanelSection: (section: NavigationPanelSection) => void;
}

const DesktopNavigationContext =
  createContext<DesktopNavigationContextType | null>(null);

interface DesktopNavigationProviderProps {
  children: ReactNode;
  defaultOpen?: boolean;
}

export function NoOpDesktopNavigationProvider({
  children,
}: DesktopNavigationProviderProps) {
  const value = useMemo(
    () => ({
      setIsNavigationBarOpen: () => {},
      toggleNavigationBar: () => {},
      isNavigationBarOpen: false,
      panelSection: "dms" as const,
      setPanelSection: () => {},
    }),
    []
  );

  return (
    <DesktopNavigationContext.Provider value={value}>
      {children}
    </DesktopNavigationContext.Provider>
  );
}

export function DesktopNavigationProvider({
  children,
  defaultOpen = true,
}: DesktopNavigationProviderProps) {
  const [isNavigationBarOpen, setIsNavigationBarOpen] = useState(defaultOpen);
  const [panelSection, setPanelSection] =
    useState<NavigationPanelSection>("dms");

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignored using `--suppress`
  const toggleNavigationBar = useCallback(() => {
    setIsNavigationBarOpen((prev) => !prev);
  }, [setIsNavigationBarOpen]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignored using `--suppress`
  const value = useMemo(
    () => ({
      setIsNavigationBarOpen,
      toggleNavigationBar,
      isNavigationBarOpen,
      panelSection,
      setPanelSection,
    }),
    [
      setIsNavigationBarOpen,
      toggleNavigationBar,
      isNavigationBarOpen,
      panelSection,
    ]
  );

  return (
    <DesktopNavigationContext.Provider value={value}>
      {children}
    </DesktopNavigationContext.Provider>
  );
}

export function useDesktopNavigation() {
  const context = useContext(DesktopNavigationContext);
  if (!context) {
    throw new Error(
      "useDesktopNavigation must be used within a DesktopNavigationProvider"
    );
  }
  return context;
}
